/**
 * Pure helpers for reasoning about a roadmap module's release date.
 *
 * Module `startDate` values are stored as `YYYY-MM-DDTHH:mm` wall-clock
 * strings in America/Los_Angeles, and `RoadmapContext` decides visibility by
 * comparing that string lexicographically against a "now" string in the
 * same format. Everything here works in that string space so the editor
 * agrees with what students are actually shown.
 */

const ROADMAP_TIME_ZONE = 'America/Los_Angeles';
const DAY_MS = 24 * 60 * 60 * 1000;

/** Where a module sits relative to now. */
export type ReleaseState = 'released' | 'scheduled' | 'unscheduled';

/**
 * Formats an instant as the roadmap's `YYYY-MM-DDTHH:mm` wall-clock string
 * in America/Los_Angeles, the same shape module `startDate` fields use.
 *
 * @param args.nowMs - The instant to format, as epoch milliseconds.
 * @return The wall-clock string, e.g. `2026-09-29T14:05`.
 * @throws Never.
 */
export function toRoadmapClockString({nowMs}: {nowMs: number}): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ROADMAP_TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(nowMs));
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find(p => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

/**
 * Classifies a module by its release date. A module is `released` when its
 * start date is at or before now (the closed lower bound
 * `startDate <= nowClock`, matching the Firestore query in
 * `RoadmapContext`), `scheduled` when it is strictly after now, and
 * `unscheduled` when it has no start date at all.
 *
 * @param args.startDate - The module's `startDate`, or `undefined`/empty when
 *   none has been set.
 * @param args.nowClock - "Now" as a roadmap clock string (see
 *   {@link toRoadmapClockString}).
 * @return The module's release state.
 * @throws Never.
 */
export function getReleaseState({
  startDate,
  nowClock,
}: {
  startDate: string | undefined;
  nowClock: string;
}): ReleaseState {
  if (!startDate) {
    return 'unscheduled';
  }
  return startDate <= nowClock ? 'released' : 'scheduled';
}

/**
 * Parses the calendar-day prefix of a roadmap clock string into a UTC
 * midnight timestamp, so two days can be subtracted without any timezone
 * conversion.
 */
function parseClockDay(clock: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(clock);
  if (!match) {
    return null;
  }
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * Whole calendar days from now's day until the module's release day, both
 * read as America/Los_Angeles wall-clock days. Positive means the release is
 * in the future, zero means today, negative means it already happened.
 *
 * @param args.startDate - The module's `startDate`.
 * @param args.nowClock - "Now" as a roadmap clock string.
 * @return The signed day count, or `null` when either string is unparseable.
 * @throws Never.
 */
export function getDaysUntilRelease({
  startDate,
  nowClock,
}: {
  startDate: string | undefined;
  nowClock: string;
}): number | null {
  if (!startDate) {
    return null;
  }
  const releaseDay = parseClockDay(startDate);
  const today = parseClockDay(nowClock);
  if (releaseDay === null || today === null) {
    return null;
  }
  return Math.round((releaseDay - today) / DAY_MS);
}

/**
 * Formats a roadmap clock string for display without shifting it into the
 * viewer's timezone, e.g. `Mon, Sep 22 · 9:00 AM`.
 *
 * @param args.startDate - The module's `startDate`.
 * @param args.locale - BCP 47 locale for month and weekday names. Defaults
 *   to `en-US` when omitted.
 * @return The formatted string, or an empty string when unparseable.
 * @throws Never.
 */
export function formatReleaseDate({
  startDate,
  locale = 'en-US',
}: {
  startDate: string | undefined;
  locale?: string;
}): string {
  if (!startDate) {
    return '';
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(startDate);
  if (!match) {
    return '';
  }
  const asUtc = new Date(
    Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4] ?? '0'),
      Number(match[5] ?? '0'),
    ),
  );
  const day = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(asUtc);
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
  }).format(asUtc);
  return `${day} · ${time}`;
}
